// Klien HTTP tipis untuk /api/v1/*. Menangani amplop error seragam
// {code, message, field_errors[], correlation_id} (Dokumen Teknis §4.1) dan
// menyisipkan Bearer access token pengguna internal.
import { useAuthStore } from '@/stores/auth'
import { kapitalkanNamaKegiatan } from '@/lib/namaKegiatan'
import router from '@/router'

export interface ApiFieldError {
  field: string
  message: string
}

export class ApiError extends Error {
  code: string
  status: number
  fieldErrors: ApiFieldError[]
  correlationId?: string
  /** Isi respons mentah — mis. versi terbaru yang menyertai 409 CONFLICT. */
  body?: Record<string, unknown>

  constructor(
    status: number,
    code: string,
    message: string,
    fieldErrors: ApiFieldError[] = [],
    correlationId?: string,
    body?: Record<string, unknown>,
  ) {
    super(message)
    this.body = body
    this.status = status
    this.code = code
    this.fieldErrors = fieldErrors
    this.correlationId = correlationId
  }
}

type RequestOptions = Omit<RequestInit, 'body'> & { body?: unknown; skipAuth?: boolean; _retried?: boolean }

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  return (await requestPenuh<T>(path, options)).data
}

// requestPenuh: seperti request, ditambah header respons (mis. X-Total-Count
// pada daftar per halaman).
async function requestPenuh<T>(path: string, options: RequestOptions = {}): Promise<{ data: T; headers: Headers }> {
  const auth = useAuthStore()
  const headers = new Headers(options.headers)
  const isFormData = options.body instanceof FormData
  // FormData tidak diberi Content-Type manual — browser mengisi boundary
  // multipart yang benar sendiri.
  if (!isFormData) {
    headers.set('Content-Type', 'application/json')
  }
  if (!options.skipAuth && auth.accessToken) {
    headers.set('Authorization', `Bearer ${auth.accessToken}`)
  }

  const res = await fetch(`/api/v1${path}`, {
    ...options,
    headers,
    body: isFormData ? (options.body as FormData) : options.body !== undefined ? JSON.stringify(options.body) : undefined,
  })

  // Retry sekali saja setelah refresh berhasil — _retried mencegah rekursi tak
  // terbatas bila endpoint tetap membalas 401 walau token baru valid.
  // Sesi ada (pengguna pernah login) → coba refresh lewat cookie HttpOnly.
  if (res.status === 401 && !options.skipAuth && !options._retried && auth.user) {
    const refreshed = await auth.tryRefresh()
    if (refreshed) {
      return requestPenuh<T>(path, { ...options, _retried: true })
    }
    await auth.logout()
    if (router.currentRoute.value.meta.requiresAuth) {
      router.push({ name: 'internal-login', query: { redirect: router.currentRoute.value.fullPath } })
    }
  }

  if (res.status === 204) {
    return { data: undefined as T, headers: res.headers }
  }

  const data = await res.json().catch(() => ({}))
  if (!res.ok) {
    throw new ApiError(res.status, data.code ?? 'UNKNOWN', data.message ?? 'Terjadi kesalahan', data.field_errors ?? [], data.correlation_id, data)
  }
  // Nama kegiatan selalu kapital di tampilan (keputusan 15 Okt 2026).
  return { data: kapitalkanNamaKegiatan(data) as T, headers: res.headers }
}

// downloadBlob dipakai untuk berkas yang butuh Authorization header (tidak
// bisa dibuka langsung via <a href> biasa) — mis. lihat berkas pendaftaran
// di panel review internal.
async function downloadBlob(path: string): Promise<Blob> {
  const auth = useAuthStore()
  const headers = new Headers()
  if (auth.accessToken) headers.set('Authorization', `Bearer ${auth.accessToken}`)
  const res = await fetch(`/api/v1${path}`, { headers })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new ApiError(res.status, data.code ?? 'DOWNLOAD_FAILED', data.message ?? 'Gagal mengunduh berkas')
  }
  return res.blob()
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) => request<T>(path, { ...options, method: 'GET' }),
  getDenganHeader: <T>(path: string, options?: RequestOptions) => requestPenuh<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) => request<T>(path, { ...options, method: 'POST', body }),
  upload: <T>(path: string, formData: FormData, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'POST', body: formData }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) => request<T>(path, { ...options, method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) => request<T>(path, { ...options, method: 'PUT', body }),
  del: <T>(path: string, options?: RequestOptions) => request<T>(path, { ...options, method: 'DELETE' }),
  downloadBlob,
}
